import { ensureGraph } from '../flow/graph';

export type SchemaFieldType = 'boolean' | 'integer' | 'number' | 'string' | 'password' | 'uint8-array' | 'json' | 'array' | 'object';

export type SchemaLayer = 'connection' | 'periphery';

export interface SchemaOption {
  value: string | number | boolean;
  label: string;
}

export interface SchemaVisibleWhen {
  field: string;
  equals: string | number | boolean;
}

export interface SchemaItemType {
  title?: string;
  layer?: SchemaLayer;
  icon?: string;
  fields: Record<string, SchemaField>;
}

export interface SchemaField {
  type: SchemaFieldType;
  title: string;
  description?: string;
  default?: unknown;
  minimum?: number;
  maximum?: number;
  options?: SchemaOption[];
  readOnly?: boolean;
  secret?: boolean;
  hidden?: boolean;
  length?: number;
  visibleWhen?: SchemaVisibleWhen;
  /** Nested object when `type` is `object`. */
  fields?: Record<string, SchemaField>;
  /** Scalar or object element when `type` is `array`. */
  item?: SchemaField | { fields: Record<string, SchemaField> };
  /** Cap on array length (GPIO relay pins: 8). */
  maxItems?: number;
  /** Discriminated objects in an array (`discriminant`, default `type`). */
  itemTypes?: Record<string, SchemaItemType>;
  discriminant?: string;
}

export interface SchemaGroup {
  id: string;
  title: string;
  /** JSON object key. Omit for root-level keys such as config_version. */
  object?: string;
  layer?: SchemaLayer;
  fields: Record<string, SchemaField>;
}

export interface ConfigSchema {
  id: string;
  title: string;
  description?: string;
  schemaVersion: number;
  configVersion?: number;
  path: string;
  groups: SchemaGroup[];
}

export type ConfigValues = Record<string, unknown>;

const FLAT_TO_NESTED: Record<string, [string, string]> = {
  hostname: ['device', 'hostname'],
  port: ['device', 'port'],
  log_level: ['device', 'log_level'],
  wifi_mode: ['wifi', 'mode'],
  wifi_client_ssid: ['wifi', 'ssid'],
  wifi_client_password: ['wifi', 'password'],
  mqtt_enable: ['mqtt', 'enable'],
  mqtt_client_server: ['mqtt', 'server'],
  mqtt_client_port: ['mqtt', 'port'],
  mqtt_client_user: ['mqtt', 'user'],
  mqtt_client_password: ['mqtt', 'password'],
  mqtt_client_topic: ['mqtt', 'topic'],
  cellular_enable: ['cellular', 'enable'],
  uplink_mode: ['cellular', 'uplink_mode'],
  cellular_apn: ['cellular', 'apn'],
  mgmt_server: ['mgmt', 'server'],
  mgmt_port: ['mgmt', 'port'],
  watchdog_enable: ['watchdog', 'enable'],
  watchdog_timeout: ['watchdog', 'timeout'],
  watchdog_misses: ['watchdog', 'misses'],
  datasaver_enable: ['datasaver', 'enable'],
  datasaver_mgmt_interval: ['datasaver', 'mgmt_interval'],
  datasaver_mqtt_interval: ['datasaver', 'mqtt_interval'],
  sensor_enable: ['sensor', 'enable'],
  sensor_sda_pin: ['sensor', 'sda_pin'],
  sensor_scl_pin: ['sensor', 'scl_pin'],
  sensor_interval: ['sensor', 'interval'],
  modbus_enable: ['modbus', 'enable'],
  modbus_baud: ['modbus', 'baud'],
  modbus_slave: ['modbus', 'slave'],
  modbus_function: ['modbus', 'function'],
  modbus_address: ['modbus', 'address'],
  modbus_count: ['modbus', 'count'],
  modbus_interval: ['modbus', 'interval'],
  // slaves[] is nested-only; flat v1 never had it
  lorawan_enable: ['lorawan', 'enable'],
  lorawan_txinterval: ['lorawan', 'txinterval'],
  lorawan_appeui: ['lorawan', 'appeui'],
  lorawan_deveui: ['lorawan', 'deveui'],
  lorawan_appkey: ['lorawan', 'appkey'],
  zigbee_enable: ['zigbee', 'enable'],
  gps_enable: ['gps', 'enable'],
  oled_enable: ['oled', 'enable'],
  display_enable: ['display', 'enable'],
  display_lvgl: ['display', 'lvgl'],
  display_panel: ['display', 'panel'],
  webserver_enable: ['webserver', 'enable'],
  ftp_enable: ['ftp', 'enable'],
  epd_enable: ['epd', 'enable'],
  epd_layout: ['epd', 'layout'],
  nmea2000_enable: ['nmea2000', 'enable'],
  serial_mode: ['serial', 'mode'],
  serial_rx_pin: ['serial', 'rx_pin'],
  serial_tx_pin: ['serial', 'tx_pin'],
  serial_baud: ['serial', 'baud'],
  power_mode: ['power', 'mode'],
  power_sleep_time: ['power', 'sleep_time'],
};

export function parseConfigJson(raw: string): ConfigValues {
  if (!raw || !raw.trim()) {
    return {};
  }
  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Config must be a JSON object');
  }
  return parsed as ConfigValues;
}

export function isFlatConfig(parsed: ConfigValues): boolean {
  return Object.keys(FLAT_TO_NESTED).some((key) => parsed[key] !== undefined);
}

export function declaredConfigVersion(parsed: ConfigValues): number | null {
  const value = Number(parsed.config_version);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export type ConfigInspect = {
  valid: boolean;
  version: number | null;
  targetVersion: number;
  flat: boolean;
  needsMigration: boolean;
  tooNew: boolean;
};

export function inspectConfigFile(raw: string, schema: ConfigSchema): ConfigInspect {
  const targetVersion = schema.configVersion ?? 0;
  if (!raw || !raw.trim()) {
    return { valid: false, version: null, targetVersion, flat: false, needsMigration: false, tooNew: false };
  }
  try {
    const parsed = parseConfigJson(raw);
    const flat = isFlatConfig(parsed);
    const version = declaredConfigVersion(parsed) ?? (flat ? 1 : targetVersion);
    return {
      valid: true,
      version,
      targetVersion,
      flat,
      needsMigration: Boolean(targetVersion) && (flat || version < targetVersion),
      tooNew: Boolean(targetVersion) && version > targetVersion,
    };
  } catch {
    return { valid: false, version: null, targetVersion, flat: false, needsMigration: false, tooNew: false };
  }
}

/**
 * Firmware from before inventory reported config_version. Nested v2 is the
 * last shape those images might understand. Do not raise this when bumping
 * schema configVersion.
 */
export const LEGACY_UNREPORTED_MAX_CONFIG_VERSION = 2;

export function deviceSupportedConfigVersion(device?: { configVersion?: number | null } | null): number {
  const value = Number(device?.configVersion);
  if (Number.isFinite(value) && value > 0) {
    return value;
  }
  return LEGACY_UNREPORTED_MAX_CONFIG_VERSION;
}

export function settingsNewerThanFirmware(fileVersion: number | null, deviceSupported: number): boolean {
  return fileVersion != null && fileVersion > deviceSupported;
}

export function groupObject(values: ConfigValues, objectKey?: string): ConfigValues | undefined {
  if (!objectKey) {
    return values;
  }
  const nested = values[objectKey];
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
    return undefined;
  }
  return nested as ConfigValues;
}

export function groupValues(values: ConfigValues, group: SchemaGroup): ConfigValues {
  if (!group.object) {
    const out: ConfigValues = {};
    for (const key of Object.keys(group.fields || {})) {
      if (values[key] !== undefined) {
        out[key] = values[key];
      }
    }
    return out;
  }
  return groupObject(values, group.object) || {};
}

export function setGroupField(values: ConfigValues, group: SchemaGroup, key: string, value: unknown): ConfigValues {
  if (!group.object) {
    return { ...values, [key]: value };
  }
  const current = groupValues(values, group);
  return { ...values, [group.object]: { ...current, [key]: value } };
}

export function fieldVisible(field: SchemaField, groupValuesMap: ConfigValues): boolean {
  if (field.hidden) {
    return false;
  }
  if (!field.visibleWhen) {
    return true;
  }
  return groupValuesMap[field.visibleWhen.field] === field.visibleWhen.equals;
}

export function isObjectItemSpec(item: SchemaField['item']): item is { fields: Record<string, SchemaField> } {
  return Boolean(item && typeof item === 'object' && 'fields' in item);
}

export function defaultsFromFields(fields: Record<string, SchemaField> | undefined): ConfigValues {
  const obj: ConfigValues = {};
  for (const [key, field] of Object.entries(fields || {})) {
    if (field.default !== undefined) {
      obj[key] = cloneValue(field.default);
    } else if (field.type !== 'number' && field.type !== 'integer') {
      obj[key] = defaultForType(field);
    }
  }
  return obj;
}

export function deviceItemTypes(schema: ConfigSchema | null | undefined): Record<string, SchemaItemType> {
  if (!schema) {
    return {};
  }
  for (const group of schema.groups) {
    const field = group.fields?.devices;
    if (field?.itemTypes) {
      return field.itemTypes;
    }
  }
  return {};
}

export function schemaGroupsForLayer(schema: ConfigSchema, layer: SchemaLayer): SchemaGroup[] {
  return schema.groups.filter((group) => group.layer === layer);
}

export function schemaGroupById(schema: ConfigSchema | null | undefined, id: string): SchemaGroup | undefined {
  return schema?.groups.find((group) => group.id === id);
}

export function schemaItemTypes(schema: ConfigSchema | null | undefined, groupId: string, fieldKey: string): Record<string, SchemaItemType> {
  return schemaGroupById(schema, groupId)?.fields?.[fieldKey]?.itemTypes || {};
}

export function defaultsFromSchema(schema: ConfigSchema): ConfigValues {
  const values: ConfigValues = {};
  for (const group of schema.groups) {
    const obj: ConfigValues = {};
    for (const [key, field] of Object.entries(group.fields || {})) {
      obj[key] = field.default !== undefined ? cloneValue(field.default) : defaultForType(field);
    }
    if (group.object) {
      values[group.object] = obj;
    } else {
      Object.assign(values, obj);
    }
  }
  values.config_version = schema.configVersion;
  return values;
}

export function migrateFlatConfig(parsed: ConfigValues): ConfigValues {
  const nested: ConfigValues = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (key === 'config_version' || FLAT_TO_NESTED[key]) {
      continue;
    }
    nested[key] = cloneValue(value);
  }
  for (const [flatKey, [objectKey, fieldKey]] of Object.entries(FLAT_TO_NESTED)) {
    if (parsed[flatKey] === undefined) {
      continue;
    }
    const current = groupObject(nested, objectKey) || {};
    nested[objectKey] = { ...current, [fieldKey]: cloneValue(parsed[flatKey]) };
  }
  return nested;
}

export function migrateConfigJson(schema: ConfigSchema, raw: string): string {
  const parsed = parseConfigJson(raw);
  const nested = isFlatConfig(parsed) ? migrateFlatConfig(parsed) : parsed;
  if (nested.modbus && typeof nested.modbus === 'object' && !Array.isArray(nested.modbus)) {
    nested.modbus = migrateModbusGroup(nested.modbus as ConfigValues);
  }
  Object.assign(nested, ensureGraph(nested));
  const values = pickSchemaValues(schema, nested, defaultsFromSchema(schema));
  return generateConfigJson(schema, values, JSON.stringify(nested));
}

function migrateModbusGroup(modbus: ConfigValues): ConfigValues {
  const enable = modbus.enable;
  const baud = modbus.baud;
  const role = modbus.role === 'slave' ? 'slave' : (modbus.role || 'master');
  if (Array.isArray(modbus.slaves) && modbus.slaves.length > 0) {
    return { enable, baud, role, slaves: cloneValue(modbus.slaves) };
  }
  const hasFlat =
    modbus.slave !== undefined ||
    modbus.function !== undefined ||
    modbus.address !== undefined ||
    modbus.count !== undefined ||
    modbus.interval !== undefined;
  if (!hasFlat) {
    return { enable, baud, role, slaves: Array.isArray(modbus.slaves) ? cloneValue(modbus.slaves) : [] };
  }
  return {
    enable,
    baud,
    role,
    slaves: [
      {
        id: 'slave1',
        name: 'Slave 1',
        unit: modbus.slave ?? 1,
        fc: modbus.function ?? 3,
        address: modbus.address ?? 0,
        count: modbus.count ?? 2,
        interval: modbus.interval ?? 60,
        points: [],
      },
    ],
  };
}

export function mergeConfig(schema: ConfigSchema, raw: string | null): ConfigValues {
  const values = defaultsFromSchema(schema);
  if (!raw || !raw.trim()) {
    return values;
  }
  const parsed = parseConfigJson(raw);
  const nested = isFlatConfig(parsed) ? migrateFlatConfig(parsed) : parsed;
  if (nested.modbus && typeof nested.modbus === 'object' && !Array.isArray(nested.modbus)) {
    nested.modbus = migrateModbusGroup(nested.modbus as ConfigValues);
  }
  Object.assign(nested, ensureGraph(nested));
  return pickSchemaValues(schema, nested, values);
}

function pickSchemaValues(schema: ConfigSchema, parsed: ConfigValues, fallback: ConfigValues): ConfigValues {
  const next: ConfigValues = {};
  for (const group of schema.groups) {
    const src = groupValues(parsed, group);
    const def = groupValues(fallback, group);
    const obj: ConfigValues = {};
    for (const key of Object.keys(group.fields || {})) {
      obj[key] = src[key] !== undefined ? src[key] : def[key];
    }
    if (group.object) {
      next[group.object] = obj;
    } else {
      Object.assign(next, obj);
    }
  }
  next.config_version = schema.configVersion;
  return next;
}

export function generateConfigJson(schema: ConfigSchema, formValues: ConfigValues, originalRaw?: string): string {
  let original: ConfigValues = {};
  if (originalRaw && originalRaw.trim()) {
    try {
      const parsed = parseConfigJson(originalRaw);
      original = isFlatConfig(parsed) ? migrateFlatConfig(parsed) : parsed;
    } catch {
      original = {};
    }
  }
  const ordered: ConfigValues = {};
  for (const group of schema.groups) {
    const src = groupValues(formValues, group);
    const orig = groupValues(original, group);
    const obj: ConfigValues = { ...orig };
    for (const key of Object.keys(group.fields || {})) {
      if (src[key] !== undefined) {
        obj[key] = src[key];
      }
    }
    if (group.object) {
      ordered[group.object] = obj;
    } else {
      Object.assign(ordered, obj);
    }
  }
  ordered.config_version = schema.configVersion;
  for (const [key, value] of Object.entries(original)) {
    if (key === 'config_version' || ordered[key] !== undefined) {
      continue;
    }
    if (schema.groups.some((group) => group.object === key || (!group.object && group.fields?.[key]))) {
      continue;
    }
    ordered[key] = value;
  }
  return `${JSON.stringify(ordered, null, 2)}\n`;
}

export function uint8ArrayToHex(value: unknown, length: number): string {
  const bytes = Array.isArray(value) ? value : [];
  const padded = Array.from({ length }, (_, i) => Number(bytes[i]) || 0);
  return padded.map((n) => Number(n).toString(16).padStart(2, '0')).join(':');
}

export function hexToUint8Array(text: string, length: number): number[] {
  const hex = text.replace(/[^0-9a-fA-F]/g, '');
  const bytes: number[] = [];
  for (let i = 0; i < length; i += 1) {
    const slice = hex.slice(i * 2, i * 2 + 2);
    bytes.push(slice ? parseInt(slice, 16) : 0);
  }
  return bytes;
}

function defaultForType(field: SchemaField): unknown {
  switch (field.type) {
    case 'boolean':
      return false;
    case 'integer':
    case 'number':
      return 0;
    case 'uint8-array':
      return Array.from({ length: field.length || 0 }, () => 0);
    case 'json':
    case 'array':
      return [];
    case 'object':
      return {};
    default:
      return '';
  }
}

function cloneValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => cloneValue(item));
  }
  if (value && typeof value === 'object') {
    const next: ConfigValues = {};
    for (const [key, item] of Object.entries(value as ConfigValues)) {
      next[key] = cloneValue(item);
    }
    return next;
  }
  return value;
}
